/**
 * «Финансы → Операции»: всё, что пришло из выписок, со статьёй. Статью меняют
 * здесь же; «правило» превращает правку в правило — и оно сразу переразносит
 * похожие операции.
 *
 * Фильтры живут в адресе: из P&L сюда приходят по ссылке «статья × ИП», из
 * «Контрагентов» — по контрагенту. `?id=…` — одна операция: ссылку дают в
 * задачу, а разбор пишут комментариями к ней.
 *
 * Комментарий к операции пишут прямо в строке, как заметку контрагента;
 * над списком — сколько в выборке не разнесено, по клику — только они.
 *
 * `?view=analytics` — вкладка «Аналитика»: та же выборка, сложенная по
 * контрагентам, с городом и итогом по городам. Клик по контрагенту — его
 * операции в этой выборке.
 */
import { Wand2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Combobox, groupOptions } from "@/components/Combobox";
import { CounterpartyTable } from "@/components/CounterpartyTable";
import { DateRangePicker, withPeriod } from "@/components/DateRangePicker";
import { EntrepreneurPicker, entrepreneurIds, withEntrepreneurs } from "@/components/EntrepreneurPicker";
import { NoAccess } from "@/components/NoAccess";
import { PNL, PNL_EDIT, useCan } from "@/lib/access";
import { mskTime } from "@/lib/dates";
import {
  addOperationComment,
  type Article,
  type BankChannel,
  CHANNEL_LABEL,
  type Counterparty,
  articleOptions,
  counterpartyOperationsUrl,
  createRule,
  loadArticles,
  loadCounterparties,
  loadOperations,
  money,
  type Operation,
  type OperationPage,
  setOperationArticle,
} from "@/lib/pnl";
import { type DirectoryRow, ENTREPRENEURS, loadDirectory } from "@/lib/statements";

const INPUT =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] outline-none focus:border-border-strong";
const TD = "border-b border-border px-2.5 py-1.5 align-top";
const LIMIT = 100;

const SOURCE: Record<Operation["article_source"], string> = {
  internal: "свой перевод",
  kassa: "выплата CRM",
  rule: "правило",
  manual: "вручную",
  none: "",
};

export function OperationsPage() {
  return useCan(PNL) ? <OperationsScreen /> : <NoAccess what="операциям" />;
}

function numberParam(value: string | null): number | undefined {
  return value ? Number(value) : undefined;
}
function OperationsScreen() {
  const canEdit = useCan(PNL_EDIT);
  const [params, setParams] = useSearchParams();
  const [articles, setArticles] = useState<Article[]>([]);
  const [entrepreneurs, setEntrepreneurs] = useState<DirectoryRow[]>([]);
  const [page, setPage] = useState<OperationPage | null>(null);
  const [failure, setFailure] = useState("");
  const [notice, setNotice] = useState("");
  const [ruleFor, setRuleFor] = useState<number | null>(null);
  const [searchDraft, setSearchDraft] = useState(params.get("search") ?? "");
  const [reloadKey, setReloadKey] = useState(0);

  const filter = useMemo(
    () => ({
      id: numberParam(params.get("id")),
      counterparty_inn: params.get("counterparty_inn") ?? undefined,
      counterparty_name: params.get("counterparty_name") ?? undefined,
      counterparty_blank: params.get("counterparty_blank") === "true",
      entrepreneur_id: entrepreneurIds(params),
      account_id: params.getAll("account_id"),
      expense: params.get("expense") === "true",
      business: params.get("business") !== "false",
      personal: params.get("personal") !== "false",
      article_id: numberParam(params.get("article_id")),
      direction: (params.get("direction") as "in" | "out" | null) ?? undefined,
      channel: (params.get("channel") as BankChannel | null) ?? undefined,
      search: params.get("search") ?? undefined,
      date_from: params.get("date_from") ?? undefined,
      date_to: params.get("date_to") ?? undefined,
      limit: LIMIT,
      offset: Number(params.get("offset") ?? 0),
    }),
    [params],
  );

  const setFilter = (key: string, value: string | number | undefined) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === "") next.delete(key);
    else next.set(key, String(value));
    if (key !== "offset") next.delete("offset");
    setParams(next);
  };

  useEffect(() => {
    loadArticles()
      .then(setArticles)
      .catch((error: Error) => setFailure(error.message));
    loadDirectory(ENTREPRENEURS, true)
      .then(setEntrepreneurs)
      .catch((error: Error) => setFailure(error.message));
  }, []);

  useEffect(() => {
    setPage(null);
    loadOperations(filter)
      .then(setPage)
      .catch((error: Error) => setFailure(error.message));
  }, [filter, reloadKey]);

  const options = articleOptions(articles);
  // «Не разнесено» — обычная статья: фильтр по ней и есть «только неразнесённые».
  const unassignedId = articles.find((a) => a.code === "unassigned")?.id;
  const onlyUnassigned = unassignedId !== undefined && filter.article_id === unassignedId;

  const replaceRow = (fresh: Operation) =>
    setPage((prev) => (prev ? { ...prev, rows: prev.rows.map((r) => (r.id === fresh.id ? fresh : r)) } : prev));

  const changeArticle = (op: Operation, articleId: number) => {
    setFailure("");
    setOperationArticle(op.id, articleId)
      .then(replaceRow)
      .catch((error: Error) => setFailure(error.message));
  };

  // Сузили до одной операции или контрагента — это видно и снимается одним кликом.
  const narrowed = filter.id
    ? `Операция №${filter.id}`
    : filter.counterparty_inn || filter.counterparty_name
      ? `Контрагент: ${filter.counterparty_inn ? `ИНН ${filter.counterparty_inn}` : filter.counterparty_name}`
      : filter.counterparty_blank
        ? "Контрагент: без названия"
        : "";
  // Тип денег пришёл из P&L — счета сужены, это видно и снимается.
  const moneyType = !filter.business
    ? filter.personal
      ? "Только личные счета"
      : "Без счетов ИП и личных"
    : filter.personal
      ? ""
      : "Только счета ИП";
  const anyMoney = () => {
    const next = new URLSearchParams(params);
    for (const key of ["business", "personal", "offset"]) next.delete(key);
    setParams(next);
  };
  // Счета и «только расходы» пришли из «Откуда ушли расходы» P&L.
  const accounts = filter.account_id.length;
  const sourced = [
    accounts ? (accounts === 1 ? "один счёт" : `счетов: ${accounts}`) : "",
    filter.expense ? "только расходы P&L" : "",
  ]
    .filter(Boolean)
    .join(", ")
    .replace(/^./, (c) => c.toUpperCase());
  const anySource = () => {
    const next = new URLSearchParams(params);
    for (const key of ["account_id", "expense", "offset"]) next.delete(key);
    setParams(next);
  };
  const analytics = params.get("view") === "analytics";
  const setView = (view: "analytics" | undefined) => {
    const next = new URLSearchParams(params);
    if (view) next.set("view", view);
    else next.delete("view");
    next.delete("offset");
    setParams(next);
  };
  // Контрагент из «Аналитики» — его операции в той же выборке.
  const openCounterparty = (cp: Counterparty) => {
    const next = new URLSearchParams(params);
    for (const key of ["view", "offset", "counterparty_inn", "counterparty_name", "counterparty_blank"]) next.delete(key);
    if (cp.inn) next.set("counterparty_inn", cp.inn);
    else if (cp.name) next.set("counterparty_name", cp.name);
    else next.set("counterparty_blank", "true");
    setParams(next);
  };

  const setPeriod = (from: string, to: string) => {
    const next = withPeriod(params, from, to);
    next.delete("offset");
    setParams(next);
  };

  const widen = () => {
    const next = new URLSearchParams(params);
    for (const key of ["id", "counterparty_inn", "counterparty_name", "counterparty_blank", "offset"]) next.delete(key);
    setParams(next);
  };

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <EntrepreneurPicker
          entrepreneurs={entrepreneurs}
          value={filter.entrepreneur_id}
          onChange={(ids) => {
            const next = withEntrepreneurs(params, ids);
            next.delete("offset");
            setParams(next);
          }}
        />
        <Combobox
          className="max-w-[280px]"
          value={String(filter.article_id ?? "")}
          onChange={(v) => setFilter("article_id", v)}
          options={[
            { value: "", label: "Все статьи" },
            ...articles
              .filter((a) => a.parent_id === null)
              .flatMap((g) => [
                { value: String(g.id), label: `${g.name} — вся группа`, group: g.name },
                ...articles
                  .filter((a) => a.parent_id === g.id)
                  .map((a) => ({ value: String(a.id), label: a.name, group: g.name })),
              ]),
          ]}
        />
        <Combobox
          value={filter.direction ?? ""}
          onChange={(v) => setFilter("direction", v)}
          options={[
            { value: "", label: "Приход и расход" },
            { value: "in", label: "Приход" },
            { value: "out", label: "Расход" },
          ]}
        />
        <DateRangePicker label="период" from={filter.date_from ?? ""} to={filter.date_to ?? ""} onChange={setPeriod} />
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            setFilter("search", searchDraft.trim());
          }}
        >
          <input
            className={`${INPUT} w-[260px]`}
            placeholder="назначение, контрагент, ИНН, счёт"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
          />
        </form>
      </div>

      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}
      {notice ? <div className="text-[12.5px] text-ok">{notice}</div> : null}
      {narrowed ? (
        <div className="flex items-center gap-2 text-[12.5px]">
          <span className="rounded-[var(--radius-ui)] bg-bg-subtle px-2 py-1">{narrowed}</span>
          <button type="button" onClick={widen} className="text-accent-text hover:underline">
            показать все
          </button>
        </div>
      ) : null}
      {moneyType ? (
        <div className="flex items-center gap-2 text-[12.5px]">
          <span className="rounded-[var(--radius-ui)] bg-bg-subtle px-2 py-1">{moneyType}</span>
          <button type="button" onClick={anyMoney} className="text-accent-text hover:underline">
            снять
          </button>
        </div>
      ) : null}
      {sourced ? (
        <div className="flex items-center gap-2 text-[12.5px]">
          <span className="rounded-[var(--radius-ui)] bg-bg-subtle px-2 py-1">{sourced}</span>
          <button type="button" onClick={anySource} className="text-accent-text hover:underline">
            снять
          </button>
        </div>
      ) : null}
      {filter.channel ? (
        <div className="flex items-center gap-2 text-[12.5px]">
          <span className="rounded-[var(--radius-ui)] bg-bg-subtle px-2 py-1">{CHANNEL_LABEL[filter.channel]}</span>
          <button type="button" onClick={() => setFilter("channel", undefined)} className="text-accent-text hover:underline">
            снять
          </button>
        </div>
      ) : null}

      <div className="flex gap-1 border-b border-border text-[12.5px]">
        {(
          [
            [undefined, "Операции"],
            ["analytics", "Аналитика по контрагентам"],
          ] as const
        ).map(([view, label]) => {
          const active = (view === "analytics") === analytics;
          return (
            <button
              key={label}
              type="button"
              onClick={() => setView(view)}
              className={`-mb-px border-b-2 px-3 py-1.5 ${
                active ? "border-fg font-medium text-fg" : "border-transparent text-fg-muted hover:text-fg"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {analytics ? (
        <Analytics filter={filter} onOpen={openCounterparty} onFailure={setFailure} />
      ) : page === null ? (
        <div className="text-[12.5px] text-fg-muted">…</div>
      ) : (
        <>
          <div className="flex items-center gap-3 text-[12px] text-fg-muted">
            <span>
              Операций: {page.total} · сальдо {money(page.sum)} ₽
            </span>
            {onlyUnassigned ? (
              <span className="flex items-center gap-2">
                <span className="rounded-[var(--radius-ui)] bg-bg-subtle px-2 py-0.5 text-fg">только неразнесённые</span>
                <button type="button" onClick={() => setFilter("article_id", undefined)} className="text-accent-text hover:underline">
                  снять
                </button>
              </span>
            ) : unassignedId !== undefined && page.unassigned ? (
              <button
                type="button"
                title="Показать только неразнесённые"
                onClick={() => setFilter("article_id", unassignedId)}
                className="text-warn hover:underline"
              >
                не разнесено: {page.unassigned}
              </button>
            ) : page.total ? (
              <span>все разнесены</span>
            ) : null}
            <Pager total={page.total} offset={filter.offset} onOffset={(o) => setFilter("offset", o || undefined)} />
          </div>
          <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
            <table className="w-full border-collapse text-[12.5px]">
              <thead>
                <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                  <th className="border-b border-border px-2.5 py-2 text-left">дата</th>
                  <th className="border-b border-border px-2.5 py-2 text-right">сумма</th>
                  <th className="border-b border-border px-2.5 py-2 text-left">счёт</th>
                  <th className="border-b border-border px-2.5 py-2 text-left">статья</th>
                  <th className="border-b border-border px-2.5 py-2 text-left">контрагент</th>
                  <th className="border-b border-border px-2.5 py-2 text-left">назначение</th>
                  <th className="border-b border-border px-2.5 py-2 text-left">комментарий</th>
                </tr>
              </thead>
              <tbody>
                {page.rows.map((op) => (
                  <OperationRow
                    key={op.id}
                    op={op}
                    options={options}
                    canEdit={canEdit}
                    ruleOpen={ruleFor === op.id}
                    onArticle={(id) => changeArticle(op, id)}
                    onRuleToggle={() => setRuleFor(ruleFor === op.id ? null : op.id)}
                    onCommented={replaceRow}
                    onRuleSaved={(changed) => {
                      setRuleFor(null);
                      setNotice(`Правило заведено, переразнесено операций: ${changed}`);
                      setReloadKey((k) => k + 1);
                    }}
                    onFailure={setFailure}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
}

/** «Аналитика»: выборка операций, сложенная по контрагентам (те же фильтры). */
function Analytics(props: {
  filter: Parameters<typeof loadCounterparties>[0];
  onOpen: (cp: Counterparty) => void;
  onFailure: (message: string) => void;
}) {
  const { filter, onFailure } = props;
  const [result, setResult] = useState<{ truncated: boolean; rows: Counterparty[] } | null>(null);
  const { search, entrepreneur_id, account_id, expense, business, personal, article_id, direction, channel, date_from, date_to } =
    filter;
  useEffect(() => {
    setResult(null);
    loadCounterparties({
      search,
      entrepreneur_id,
      account_id,
      expense,
      business,
      personal,
      article_id,
      direction,
      channel,
      date_from,
      date_to,
    })
      .then(setResult)
      .catch((error: Error) => onFailure(error.message));
  }, [search, entrepreneur_id, account_id, expense, business, personal, article_id, direction, channel, date_from, date_to, onFailure]);

  if (result === null) return <div className="text-[12.5px] text-fg-muted">…</div>;
  if (result.rows.length === 0) return <div className="text-[12.5px] text-fg-muted">Никого не нашлось.</div>;
  return <CounterpartyTable result={result} onResult={setResult} onOpen={props.onOpen} onFailure={onFailure} />;
}

function Pager(props: { total: number; offset: number; onOffset: (offset: number) => void }) {
  if (props.total <= LIMIT) return null;
  const last = props.offset + LIMIT >= props.total;
  return (
    <span className="ml-auto flex items-center gap-2">
      <button
        type="button"
        disabled={props.offset === 0}
        onClick={() => props.onOffset(Math.max(0, props.offset - LIMIT))}
        className="rounded border border-border px-2 py-0.5 disabled:opacity-40"
      >
        ←
      </button>
      {props.offset + 1}–{Math.min(props.offset + LIMIT, props.total)} из {props.total}
      <button
        type="button"
        disabled={last}
        onClick={() => props.onOffset(props.offset + LIMIT)}
        className="rounded border border-border px-2 py-0.5 disabled:opacity-40"
      >
        →
      </button>
    </span>
  );
}

function OperationRow(props: {
  op: Operation;
  options: { group: string; items: Article[] }[];
  canEdit: boolean;
  ruleOpen: boolean;
  onArticle: (id: number) => void;
  onRuleToggle: () => void;
  onCommented: (fresh: Operation) => void;
  onRuleSaved: (changed: number) => void;
  onFailure: (message: string) => void;
}) {
  const { op } = props;
  const unassigned = op.article_source === "none";
  return (
    <>
      <tr data-annot={`операция ${op.id}`} className="hover:bg-bg-hover">
        <td className={`${TD} whitespace-nowrap tabular-nums text-fg-muted`}>
          {op.date.split("-").reverse().join(".")}
          {/* Постоянная ссылка на операцию — её дают в задачу. */}
          <Link to={`/pnl/operations?id=${op.id}`} className="block text-[10.5px] text-fg-faint hover:text-accent-text">
            №{op.id}
          </Link>
        </td>
        <td className={`${TD} whitespace-nowrap text-right tabular-nums ${op.direction === "in" ? "text-ok" : ""}`}>
          {op.direction === "in" ? "+" : "−"}
          {money(op.amount)}
        </td>
        <td className={`${TD} whitespace-nowrap text-[11.5px] text-fg-muted`}>
          <div className="tabular-nums">{op.account}</div>
          <div className="text-fg-faint">
            {op.entrepreneur_name}
            {op.account_kind === "personal" ? " · физлицо" : ""}
          </div>
        </td>
        <td className={`${TD} min-w-[220px]`}>
          {props.canEdit ? (
            <div className="flex items-center gap-1">
              <Combobox
                size="sm"
                className="w-full"
                warn={unassigned}
                value={String(op.article_id)}
                onChange={(v) => props.onArticle(Number(v))}
                options={groupOptions(props.options)}
              />
              <button
                type="button"
                title="Сделать правило по этой операции"
                onClick={props.onRuleToggle}
                className="rounded p-1 text-fg-faint hover:bg-bg-hover hover:text-fg"
              >
                <Wand2 size={13} />
              </button>
            </div>
          ) : (
            <span className={unassigned ? "text-warn" : ""}>{op.article_name}</span>
          )}
          {SOURCE[op.article_source] ? (
            <div className="mt-0.5 text-[10.5px] text-fg-faint">{SOURCE[op.article_source]}</div>
          ) : null}
          {op.kassa_expense ? (
            <div
              className="mt-0.5 text-[10.5px] text-fg-faint"
              title={[
                `выплата CRM ${op.kassa_expense.date.slice(0, 10)} на ${op.kassa_expense.amount}`,
                op.kassa_expense.crm_article_name,
                op.kassa_expense.owner_crm_id ? `владелец №${op.kassa_expense.owner_crm_id}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            >
              CRM №{op.kassa_expense.crm_id}
              {op.kassa_expense.recipient_name ? ` · ${op.kassa_expense.recipient_name}` : ""}
            </div>
          ) : null}
        </td>
        <td className={`${TD} max-w-[240px]`}>
          {op.counterparty_inn || op.counterparty_name ? (
            <Link
              to={counterpartyOperationsUrl({ inn: op.counterparty_inn, name: op.counterparty_name })}
              className="block truncate hover:text-accent-text hover:underline"
              title={`${op.counterparty_name} — все операции с ним`}
            >
              {op.counterparty_name || "—"}
            </Link>
          ) : (
            <div>—</div>
          )}
          {op.counterparty_inn || op.counterparty_location_name ? (
            <div className="text-[11px] text-fg-faint">
              {op.counterparty_inn ? `ИНН ${op.counterparty_inn}` : ""}
              {op.counterparty_inn && op.counterparty_location_name ? " · " : ""}
              {op.counterparty_location_name ? (
                <span className="text-fg-muted" title="Город контрагента — меняется во вкладке «Аналитика»">
                  {op.counterparty_location_name}
                </span>
              ) : null}
            </div>
          ) : null}
        </td>
        <td className={`${TD} max-w-[420px] text-fg-muted`}>
          <div className="line-clamp-2" title={op.purpose}>
            {op.purpose}
          </div>
        </td>
        <td className={`${TD} min-w-[200px] max-w-[300px]`}>
          {op.comments.map((c) => (
            <div key={c.id} className="mb-1 rounded-[var(--radius-ui)] bg-bg-subtle px-2 py-1 text-[12px] text-fg">
              <span className="whitespace-pre-wrap">{c.text}</span>
              <span className="ml-1.5 text-[10.5px] text-fg-faint">
                — {c.user_name}, {mskTime(c.date)}
              </span>
            </div>
          ))}
          {props.canEdit ? (
            <CommentInput op={op} onSaved={props.onCommented} onFailure={props.onFailure} />
          ) : op.comments.length ? null : (
            <span className="text-fg-faint">—</span>
          )}
        </td>
      </tr>
      {props.ruleOpen ? (
        <tr>
          <td colSpan={7} className="border-b border-border bg-bg-subtle px-3 py-3">
            <RuleForm op={op} options={props.options} onSaved={props.onRuleSaved} onFailure={props.onFailure} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * Поле комментария в строке — как заметка контрагента: Enter или уход из поля
 * добавляет комментарий в ленту операции, Esc — отмена. Лента не затирается.
 */
function CommentInput(props: { op: Operation; onSaved: (fresh: Operation) => void; onFailure: (m: string) => void }) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  // Esc: blur() приходит раньше, чем сброс черновика — сохранять нечего.
  const cancelled = useRef(false);
  const commit = () => {
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    addOperationComment(props.op.id, text)
      .then((fresh) => {
        setDraft("");
        props.onSaved(fresh);
      })
      .catch((error: Error) => props.onFailure(error.message))
      .finally(() => setBusy(false));
  };
  return (
    <input
      className="h-7 w-full rounded-[var(--radius-ui)] border border-border bg-bg px-1.5 text-[12px] outline-none placeholder:text-fg-faint focus:border-border-strong disabled:opacity-50"
      placeholder="что это, куда разнести"
      maxLength={4000}
      disabled={busy}
      value={draft}
      title={draft || undefined}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          cancelled.current = true;
          setDraft("");
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/** Экранировать текст для регэкспа правила: назначение — это не шаблон. */
function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function RuleForm(props: {
  op: Operation;
  options: { group: string; items: Article[] }[];
  onSaved: (changed: number) => void;
  onFailure: (message: string) => void;
}) {
  const { op } = props;
  const [articleId, setArticleId] = useState(op.article_source === "none" ? 0 : op.article_id);
  const [byInn, setByInn] = useState(Boolean(op.counterparty_inn));
  const [purpose, setPurpose] = useState(op.counterparty_inn ? "" : escapeRegex(op.purpose.slice(0, 40)));
  const [busy, setBusy] = useState(false);

  const save = () => {
    setBusy(true);
    createRule({
      article_id: articleId,
      direction: op.direction,
      counterparty_inn: byInn && op.counterparty_inn ? op.counterparty_inn : undefined,
      purpose_pattern: purpose.trim() || undefined,
    })
      .then((r) => props.onSaved(r.changed))
      .catch((error: Error) => props.onFailure(error.message))
      .finally(() => setBusy(false));
  };

  return (
    <div className="grid max-w-[820px] gap-2 text-[12.5px]">
      <div className="text-fg-muted">
        Правило для {op.direction === "in" ? "приходов" : "расходов"}: все условия должны совпасть; сработает и на
        прошлых, и на будущих операциях (кроме поставленных вручную).
      </div>
      {op.counterparty_inn ? (
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={byInn} onChange={(e) => setByInn(e.target.checked)} />
          контрагент — ИНН {op.counterparty_inn} ({op.counterparty_name})
        </label>
      ) : null}
      <label className="grid gap-1">
        <span className="text-fg-faint">назначение содержит (регэксп, без учёта регистра; пусто — любое)</span>
        <input className={INPUT} value={purpose} onChange={(e) => setPurpose(e.target.value)} />
      </label>
      <div className="flex items-center gap-2">
        <Combobox
          className="min-w-[220px]"
          placeholder="— статья —"
          value={articleId ? String(articleId) : ""}
          onChange={(v) => setArticleId(Number(v))}
          options={groupOptions(
            props.options.map((g) => ({ ...g, items: g.items.filter((a) => a.code !== "unassigned") })),
          )}
        />
        <button
          type="button"
          disabled={!articleId || busy || (!purpose.trim() && !(byInn && op.counterparty_inn))}
          onClick={save}
          className="h-8 rounded-[var(--radius-ui)] bg-fg px-3 font-medium text-bg disabled:opacity-50"
        >
          {busy ? "Сохраняем…" : "Завести правило"}
        </button>
      </div>
    </div>
  );
}
